
import { useCallback } from 'react';
import { LocationState, LocationConfig, PromptState, STORAGE_KEYS } from './types';
import { setToStorage } from './storage-utils';
import { usePromptState, useDoNotShowAgainState } from './use-location-state';
import { createLogger } from "@/utils/logger";

const logger = createLogger('location:prompts');

/**
 * Hook for managing location prompts
 */
export const useLocationPrompts = (
  locationState: LocationState,
  config: LocationConfig
) => {
  const { promptState, setPromptState } = usePromptState();
  const { doNotShowAgain, setDoNotShowAgain } = useDoNotShowAgainState();

  // Track user interactions with debouncing
  const trackInteraction = useCallback((type?: string) => {
    // Don't increase interactions if user set "do not show again"
    if (doNotShowAgain) return;
    
    const newCount = promptState.interactionCount + 1;
    setToStorage(STORAGE_KEYS.INTERACTIONS, newCount);
    
    setPromptState(prev => ({
      ...prev,
      interactionCount: newCount
    }));
    
    logger.debug(`User interaction tracked: ${type || 'unspecified'}, count: ${newCount}`);
  }, [promptState.interactionCount, doNotShowAgain, setPromptState]);

  // Function to check if we should show the location prompt
  const shouldShowLocationPrompt = useCallback((contextual: boolean = false) => {
    const now = Date.now();
    
    // Don't show if user already granted or denied permission
    if (locationState.permission !== 'prompt') {
      return false;
    }
    
    // Don't show if in cooldown period
    if (promptState.cooldownEndTime && now < promptState.cooldownEndTime) {
      return false;
    }
    
    // Don't show if we've hit the maximum count
    if (promptState.promptCount >= config.maxPromptFrequency) {
      return false;
    }
    
    // Don't show if user set "do not show again"
    if (doNotShowAgain) {
      return false;
    }
    
    // For contextual prompts, be more aggressive
    if (contextual) {
      return true;
    }
    
    // For non-contextual prompts, require minimum interactions
    return promptState.interactionCount >= config.minInteractionsBeforePrompt;
  }, [
    locationState.permission,
    promptState,
    doNotShowAgain,
    config
  ]);

  // Record that we showed the prompt
  const recordPromptShown = useCallback(() => {
    const now = Date.now();
    const newPromptCount = promptState.promptCount + 1;
    
    setToStorage(STORAGE_KEYS.LAST_PROMPT, now);
    setToStorage(STORAGE_KEYS.PROMPT_COUNT, newPromptCount);
    
    setPromptState(prev => ({
      ...prev,
      lastPromptTime: now,
      promptCount: newPromptCount,
      hasShownPrompt: true
    }));
    
    logger.debug(`Location prompt shown, count: ${newPromptCount}`);
  }, [promptState, setPromptState]);

  // Set "maybe later" state
  const setMaybeLater = useCallback(() => {
    const now = Date.now();
    const cooldownEnd = now + (config.cooldownPeriod * 60 * 60 * 1000);
    
    setToStorage(STORAGE_KEYS.COOLDOWN, cooldownEnd);
    
    setPromptState(prev => ({
      ...prev,
      hasShownPrompt: true,
      cooldownEndTime: cooldownEnd
    }));
    
    trackInteraction('maybe_later');
    
    logger.debug('Location prompt dismissed with "maybe later"');
  }, [config.cooldownPeriod, setPromptState, trackInteraction]);

  // Method to set "do not show again" preference
  const setDoNotShowAgainPreference = useCallback((value: boolean) => {
    setToStorage(STORAGE_KEYS.DO_NOT_SHOW_AGAIN, value);
    setDoNotShowAgain(value);
    logger.debug(`Do not show location prompts again set to: ${value}`);
  }, [setDoNotShowAgain]);

  return {
    promptState,
    setPromptState,
    doNotShowAgain,
    setDoNotShowAgain,
    trackInteraction,
    shouldShowLocationPrompt,
    recordPromptShown,
    setMaybeLater,
    setDoNotShowAgainPreference,
  };
};
